import { useEffect, useState } from "react";
import { AuthClient } from "@/clients";
import { User } from "@/types/UserTypes";
import { useForm } from "react-hook-form";
import {
  Loader2,
  Save,
  User as UserIcon,
  MapPin,
  Phone,
  Mail,
} from "lucide-react";
import { toast } from "sonner";
import { useAuthStore } from "@/store/authStore";
import PageHeader from "@/components/ui/PageHeader";
import ChangePassword from "./ChangePassword";

export default function Profile() {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const setUserDetails = useAuthStore((state) => state.setUserDetails);
  const userDetails = useAuthStore((state) => state.userDetails);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<User>();

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = await AuthClient.getOwnUser();
        // Ensure mobile_number starts with +63 or is initialized to +63
        if (!userData.mobile_number) {
          userData.mobile_number = "+63";
        } else if (!userData.mobile_number.startsWith("+63")) {
          if (userData.mobile_number.startsWith("0")) {
            userData.mobile_number =
              "+63" + userData.mobile_number.substring(1);
          } else {
            userData.mobile_number = "+63" + userData.mobile_number;
          }
        }

        setUser(userData);
        reset(userData);
        // If address exists, set it (handling the array structure)
        if (userData.addresses && userData.addresses.length > 0) {
          const primaryAddress = userData.addresses[0];
          setValue("addresses.0.street", primaryAddress.street);
          setValue("addresses.0.city", primaryAddress.city);
          setValue("addresses.0.state", primaryAddress.state);
          setValue("addresses.0.postal_code", primaryAddress.postal_code);
          setValue("addresses.0.region", primaryAddress.region);
        }
      } catch (error) {
        console.error("Failed to fetch user profile:", error);
        toast.error("Failed to load profile");
      } finally {
        setIsLoading(false);
      }
    };

    fetchUser();
  }, [reset, setValue]);

  const onSubmit = async (data: User) => {
    setIsSaving(true);
    try {
      await AuthClient.updateOwnUser(data);
      setUser(data);
      setUserDetails({
        ...userDetails,
        firstName: data.first_name,
        lastName: data.last_name,
      });
      toast.success("Profile updated successfully");
    } catch (error) {
      console.error("Failed to update profile:", error);
      toast.error("Failed to update profile");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="container">
      <div className="md:my-10 my-4 md:mx-0 mx-2">
        <PageHeader
          title="My Profile"
          subtitle="Manage your personal information and preferences"
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Sidebar / Summary Card */}
          <div className="md:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
              <div className="w-24 h-24 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4 text-primary-600">
                {user?.image_url ? (
                  <img
                    src={user.image_url}
                    alt="Profile"
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <UserIcon size={40} />
                )}
              </div>
              <h2 className="text-xl font-semibold text-gray-900">
                {user?.first_name} {user?.last_name}
              </h2>
              <p className="text-gray-500 text-sm mt-1">{user?.email}</p>

              <div className="mt-6 pt-6 border-t border-gray-100 text-left space-y-3">
                <div className="flex items-center text-sm text-gray-600">
                  <Mail size={16} className="mr-3 text-gray-400" />
                  <span className="truncate">{user?.email}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Phone size={16} className="mr-3 text-gray-400" />
                  <span>{user?.mobile_number || "No phone number"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Main Form */}
          <div className="md:col-span-2 space-y-8">
            {/* Personal Information Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">
                Personal Information
              </h3>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name
                    </label>
                    <input
                      {...register("first_name", {
                        required: "First name is required",
                      })}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all"
                    />
                    {errors.first_name && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.first_name.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name
                    </label>
                    <input
                      {...register("last_name", {
                        required: "Last name is required",
                      })}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all"
                    />
                    {errors.last_name && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.last_name.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address
                    </label>
                    <input
                      {...register("email")}
                      disabled
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 bg-gray-50 text-gray-500 cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Email cannot be changed
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Phone className="text-gray-400" size={20} />
                      </div>
                      <input
                        {...register("mobile_number", {
                          required: "Phone number is required",
                          pattern: {
                            value: /^\+63\d{10}$/,
                            message:
                              "Please enter a valid 10-digit phone number",
                          },
                        })}
                        onChange={(e) => {
                          const value = e.target.value;
                          let newValue = value;

                          // If user is trying to delete the +63 prefix, allow it but keep it minimal
                          if (
                            value === "" ||
                            value === "+" ||
                            value === "+6" ||
                            value === "+63"
                          ) {
                            newValue = "+63";
                          } else if (!value.startsWith("+63")) {
                            const digitsOnly = value.replace(/\D/g, "");
                            const limitedDigits = digitsOnly.slice(0, 10);
                            newValue = limitedDigits
                              ? `+63${limitedDigits}`
                              : "+63";
                          } else {
                            const afterPrefix = value.substring(3);
                            const digitsOnly = afterPrefix.replace(/\D/g, "");
                            const limitedDigits = digitsOnly.slice(0, 10);
                            newValue = `+63${limitedDigits}`;
                          }

                          setValue("mobile_number", newValue, {
                            shouldValidate: true,
                          });
                        }}
                        className={`w-full pl-10 pr-4 py-2 rounded-lg border focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all ${
                          errors.mobile_number
                            ? "border-red-300"
                            : "border-gray-300"
                        }`}
                        placeholder="+63XXXXXXXXXX"
                      />
                    </div>
                    {errors.mobile_number && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.mobile_number.message}
                      </p>
                    )}
                    {!errors.mobile_number && (
                      <p className="text-gray-500 text-xs mt-1">
                        Enter your 10-digit phone number (e.g., +639123456789)
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end pt-6">
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="flex items-center space-x-2 bg-primary-600 text-white px-6 py-2.5 rounded-lg hover:bg-primary-700 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
                  >
                    {isSaving ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Save size={20} />
                    )}
                    <span>
                      {isSaving ? "Saving Changes..." : "Save Changes"}
                    </span>
                  </button>
                </div>
              </form>
            </div>

            {/* Change Password Section */}
            <ChangePassword />
          </div>
        </div>
      </div>
    </div>
  );
}
